import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Calendar, Plus, Euro, ChefHat } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function PlansPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const householdRes = await axios.get(`${API}/households`, { withCredentials: true });
      const hh = householdRes.data.household;
      if (hh) {
        const plansRes = await axios.get(`${API}/week-plans/${hh.household_id}`, { withCredentials: true });
        setPlans(plansRes.data.plans);
      }
    } catch (error) {
      console.error('Failed to fetch plans:', error);
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="py-4 sm:py-8">
      <div className="container mx-auto px-3 sm:px-4 max-w-6xl">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 sm:mb-8">
          <div className="flex items-center gap-3">
            <div className="bg-primary rounded-full p-2 sm:p-3">
              <Calendar className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
            </div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-[#2C3E2D]" data-testid="plans-title">
              {t('plans.title')}
            </h1>
          </div>
          <Button
            onClick={() => navigate('/plan/create')}
            className="bg-secondary hover:bg-secondary-hover text-white w-full sm:w-auto"
            data-testid="create-plan-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('plans.new_plan')}
          </Button>
        </div>

        {plans.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            {plans.map((plan) => (
              <Card 
                key={plan.plan_id} 
                className="hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => navigate(`/plan/${plan.plan_id}`)}
                data-testid={`plan-card-${plan.plan_id}`}
              >
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>{t('plans.week_of', { date: plan.start_date })}</span>
                    <Calendar className="w-5 h-5 text-primary" />
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
                      <div>
                        <p className="text-sm text-gray-600">{t('plans.total_cost')}</p>
                        <p className="text-2xl font-bold text-primary">&euro;{plan.total_cost_estimate_eur?.toFixed(2)}</p>
                      </div>
                      <Euro className="w-8 h-8 text-gray-400" />
                    </div>

                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
                      <div>
                        <p className="text-sm text-gray-600">{t('plans.savings')}</p>
                        <p className={`text-2xl font-bold ${plan.saving_estimate_vs_country_avg_eur >= 0 ? 'text-success' : 'text-destructive'}`}>
                          {plan.saving_estimate_vs_country_avg_eur >= 0 ? '+' : ''}&euro;{plan.saving_estimate_vs_country_avg_eur?.toFixed(2)}
                        </p>
                      </div>
                      <ChefHat className="w-8 h-8 text-gray-400" />
                    </div>

                    <div className="pt-2">
                      <p className="text-sm text-gray-600 mb-2">{t('plans.dishes')}</p>
                      <p className="font-semibold">{t('plans.days_planned', { count: plan.dishes_per_day?.length || 0 })}</p>
                    </div>

                    <Button variant="outline" className="w-full" onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/shopping/${plan.plan_id}`);
                    }} data-testid={`view-shopping-${plan.plan_id}`}>
                      {t('plans.view_shopping')}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-600 mb-2">{t('plans.no_plans')}</h3>
            <p className="text-gray-500 mb-6">{t('plans.no_plans_desc')}</p>
            <Button onClick={() => navigate('/plan/create')} className="bg-primary hover:bg-primary-hover text-white" data-testid="create-first-plan-btn">
              <Plus className="w-4 h-4 mr-2" />
              {t('plans.create_first')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

export default PlansPage;
